/*
 * Pure helpers behind the case header: display strings and deadline urgency.
 * No React, no IO, so they are unit-tested directly.
 */
import {
  getWorkflowType,
  isNoirNoidWorkflow,
  isRfeLikeWorkflow,
  type WorkflowType
} from "../../../types/workflow.type";

export { isWorkspacePath } from "../routes";

/** Full name from whatever name parts exist; empty string when none. */
export const displayName = (parts: Array<string | null | undefined>): string =>
  parts
    .map((p) => (p ?? "").toString().trim())
    .filter(Boolean)
    .join(" ");

export const initialsOf = (name: string, fallback = "CL"): string => {
  const parts = name.split(" ").filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase() || fallback;
};

/**
 * A stored visa value as the attorney knows it: internal template versions
 * ("EB-2 NIW v2") read as the visa ("EB-2 NIW").
 */
export const visaDisplayName = (visa: string): string =>
  visa.replace(/\s+v\d+$/i, "");

/**
 * Workflow label shown in the header: the case's visa (falling back to the
 * individual's), suffixed with " RFE" / " NOID/NOIR" for notice workflows
 * since those share the base visa (mirrors VisaTag). Never read from the URL.
 */
export const workflowLabel = (args: {
  caseVisa?: string | null;
  individualVisa?: string | null;
  workflowType?: WorkflowType | string | null;
  isRfe?: boolean | null;
  /** "Custom: <name>" for a case of a named custom case type. */
  customLabel?: string | null;
}): string => {
  const base =
    args.customLabel ||
    visaDisplayName(
      (args.caseVisa ?? "").trim() || (args.individualVisa ?? "").trim()
    );
  if (!base) return "";
  const kind = getWorkflowType(args.workflowType, Boolean(args.isRfe));
  if (!isRfeLikeWorkflow(kind)) return base;
  return `${base}${isNoirNoidWorkflow(kind) ? " NOID/NOIR" : " RFE"}`;
};

export type DeadlineUrgency = "none" | "ok" | "soon" | "overdue";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The calendar day a stored date means, as local midnight. Date-only fields
 * are saved as midnight or noon UTC and the API may send them as
 * "Sun, 31 May 2026 00:00:00 GMT", so those keep their UTC day rather than
 * sliding to the day before west of Greenwich. Null when invalid.
 */
export const calendarDay = (value?: string | null): Date | null => {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const utcMs = d.getTime() % DAY_MS;
  if (utcMs === 0 || utcMs === DAY_MS / 2) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return d;
};

/** Whole days from `now` to the deadline (negative when past). */
export const daysUntil = (
  iso: string,
  now: Date = new Date()
): number | null => {
  const d = calendarDay(iso);
  if (!d) return null;
  const start = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((start(d) - start(now)) / DAY_MS);
};

/** "soon" inside 14 days, "overdue" once the day has passed. */
export const deadlineUrgency = (
  iso?: string | null,
  now: Date = new Date()
): DeadlineUrgency => {
  if (!iso) return "none";
  const days = daysUntil(iso, now);
  if (days === null) return "none";
  if (days < 0) return "overdue";
  if (days <= 14) return "soon";
  return "ok";
};

export const formatDeadline = (iso?: string | null): string => {
  const d = calendarDay(iso);
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/** Relative phrasing for the chip tooltip: "in 3 days", "today", "5 days ago". */
export const relativeDeadline = (
  iso?: string | null,
  now: Date = new Date()
): string => {
  if (!iso) return "";
  const days = daysUntil(iso, now);
  if (days === null) return "";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
};

/** ISO string pinned to noon UTC so the calendar day survives timezone shifts. */
export const isoAtNoonUtc = (date: Date): string => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12)
  );
  return d.toISOString();
};

/** `YYYY-MM-DD` for a native date input, or "" when unset/invalid. */
export const toDateInputValue = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

/** USCIS's service center for a receipt number, read off its prefix. */
const SERVICE_CENTERS: Record<string, string> = {
  EAC: "Vermont Service Center",
  VSC: "Vermont Service Center",
  WAC: "California Service Center",
  CSC: "California Service Center",
  LIN: "Nebraska Service Center",
  NSC: "Nebraska Service Center",
  SRC: "Texas Service Center",
  TSC: "Texas Service Center",
  YSC: "Potomac Service Center",
  MSC: "National Benefits Center",
  NBC: "National Benefits Center",
  IOE: "USCIS online (ELIS)"
};

export const serviceCenterFor = (receipt?: string | null): string | null =>
  SERVICE_CENTERS[(receipt ?? "").trim().slice(0, 3).toUpperCase()] ?? null;

export type CaseDeadline = { label: string; date: string };

type FactLike = {
  kind: string;
  label: string;
  display: string;
  status: string;
  value: unknown;
};

const record = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

const usable = (fact: FactLike) =>
  fact.status !== "disabled" && fact.status !== "failed";

/**
 * Dates the key facts derive: a status or passport running out
 * (`value.deadlines`), a notice's response deadline. Soonest first. Only the
 * notice fact itself counts for the response deadline: each of its issues
 * (`notice_issue`) carries a copy of the same date.
 */
export const keyFactDeadlines = (
  facts: readonly FactLike[]
): CaseDeadline[] => {
  const out: CaseDeadline[] = [];
  facts.filter(usable).forEach((fact) => {
    const value = record(fact.value);
    if (!value) return;
    const listed = Array.isArray(value.deadlines) ? value.deadlines : [];
    listed.forEach((d) => {
      const row = record(d);
      const date = text(row?.date);
      if (date) out.push({ label: text(row?.label) || fact.label, date });
    });
    const respondBy =
      fact.kind === "notice" ? text(value.response_deadline) : "";
    if (respondBy) {
      const what = fact.display.split(";")[0]?.trim() || fact.label;
      out.push({ label: `Respond to ${what}`, date: respondBy });
    }
  });
  const seen = new Set<string>();
  return out
    .filter((d) => !Number.isNaN(new Date(d.date).getTime()))
    .filter((d) => {
      const key = `${d.label}|${d.date}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export type ImmigrationSummary = {
  receipts: string[];
  serviceCenter: string | null;
  priorityDate: string | null;
  statusExpiry: string | null;
  premiumProcessing: boolean;
  deadlines: CaseDeadline[];
};

/**
 * The filing facts the header shows: tracked receipts (else those read off
 * notices), the service center (a notice's, else the receipt prefix's), the
 * client's priority date and status expiry (else the case's legacy copies),
 * and the deadlines the key facts derive.
 */
export const immigrationSummary = (args: {
  facts: readonly FactLike[];
  trackedReceipts: readonly string[];
  client?: {
    priority_date?: string | null;
    immigration_status_expiration?: string | null;
  } | null;
  caseInfo?: {
    priority_date?: string | null;
    immigration_status_expiration?: string | null;
    premium_processing?: boolean | null;
  } | null;
}): ImmigrationSummary => {
  const notices = args.facts
    .filter(usable)
    .map((f) => record(f.value))
    .filter((v): v is Record<string, unknown> => !!v && "notice_type" in v);
  const noticeReceipts = notices.map((v) => text(v.receipt_number));
  const receipts = [
    ...new Set(
      [...args.trackedReceipts, ...noticeReceipts]
        .map((r) => r.trim().toUpperCase())
        .filter(Boolean)
    )
  ];
  const noticeCenter = notices.map((v) => text(v.service_center)).find(Boolean);
  return {
    receipts,
    serviceCenter: noticeCenter || serviceCenterFor(receipts[0]),
    priorityDate:
      args.client?.priority_date || args.caseInfo?.priority_date || null,
    statusExpiry:
      args.client?.immigration_status_expiration ||
      args.caseInfo?.immigration_status_expiration ||
      null,
    premiumProcessing: Boolean(args.caseInfo?.premium_processing),
    deadlines: keyFactDeadlines(args.facts)
  };
};
